/**
This calculator.ts file will be imported from the shared-libs folder as soon as it has been published from the npm package
*/
import Big from '@swyftx/big';

export type EstimatedLoanSummary = {
  loanAmount: string;
  originationFee: string;
  maxInterest: string;
  totalRepayment: string;
  securityRequired: string;
  securityInFiat: string;
  notificationMessage?: string; // Added new field
};
export const MIN_ESTIMATED_LOAN_AMOUNT = 250; // 250 AUD
export const MAX_ESTIMATED_LOAN_AMOUNT = 5_000_000;

/*
These values are yet to be finalised and will most probably be stored in the database.
We may also actually let them be passed through by the client
*/
export const MAX_LVR = new Big(0.4); // 40% LVR
export const ORIGINATION_FEE = new Big(0.02); // 2%
export const INTEREST = new Big(0.095); // 9.5% max interest

/**
 * Invalid inputs will be treated as 0, unless they are prefixed with a numerical value, in which case the numerical value will be used
 */

export const estimateLoanSummary = ({
  fiat,
  rate,
  availableBalance,
}: {
  fiat: string;
  rate: string;
  availableBalance?: string; // Added availableBalance
}): EstimatedLoanSummary => {
  const bigFiat = new Big(parseFloat(fiat.replace(/,/g, '')) || 0);
  const securityRequired = bigFiat.div(rate).div(MAX_LVR);
  let notificationMessage = '';

  if (availableBalance) {
    const bigAvailableBalance = new Big(availableBalance);
    if (securityRequired.gt(0) && bigAvailableBalance.gt(0)) {
      notificationMessage = `Your portfolio covers ${bigAvailableBalance
        .div(securityRequired)
        .toFixed(8)}% of the required collateral`;
    } else if (bigAvailableBalance.eq(0)) {
      notificationMessage = 'You have no BTC available in your portfolio';
    }
  }

  return {
    loanAmount: bigFiat.toFixed(2),
    originationFee: bigFiat.times(ORIGINATION_FEE).toFixed(2),
    maxInterest: bigFiat.times(INTEREST).toFixed(2),
    totalRepayment: bigFiat
      .times(ORIGINATION_FEE)
      .plus(bigFiat.plus(bigFiat.times(INTEREST)))
      .toFixed(2),
    securityRequired: securityRequired.toFixed(8),
    securityInFiat: bigFiat.div(MAX_LVR).toFixed(2),
    notificationMessage, // Added notificationMessage
  };
};

export const indicativeFiatFromSecurity = (security: string, rate: number): string => {
  const value = new Big(parseFloat(security.replace(/,/g, '')) || 0);
  return value.times(rate).toFixed(2);
};

export const indicativeSecurityFromFiat = (fiat: string, rate: number): string => {
  const value = new Big(parseFloat(fiat.replace(/,/g, '')) || 0);
  return value.div(rate).toFixed(8);
};
